import { Trans, useLingui } from "@lingui/react/macro";
import Form from "@/components/Form/Form";
import { useFormContext, useFieldArray } from "react-hook-form";
import { PlainButton } from "@medoma/components/Button/Button";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import InputField from "@medoma/components/InputField/InputField";
import Checkbox from "@/components/Checkbox/Checkbox";
import type { IFormRelative } from "@medoma/models/patients";
import { generateRandomUUID } from "@/Utils/UniqueId";
import {
  informationFieldConfigValueSchema,
  type IInformationFieldConfigValue,
} from "@/api/Patients";
import styles from "./RelativesInput.module.scss";

export type RelativesInputFields = {
  relatives: IFormRelative[];
};

const RelativeInput = ({
  onRemove,
  index,
}: {
  onRemove: () => void;
  index: number;
}) => {
  const {
    formState: { errors },
    register,
  } = useFormContext<RelativesInputFields>();
  const { t } = useLingui();

  return (
    <Form.Section>
      <InputField
        label={t`Namn`}
        showOptionalLabel={false}
        errorMessage={errors.relatives?.[index]?.name?.message}
        {...register(`relatives.${index}.name`, {
          required: {
            value: true,
            message: t`Ange namn`,
          },
        })}
      />
      <InputField
        label={t`Telefonnummer`}
        showOptionalLabel={true}
        errorMessage={errors.relatives?.[index]?.phoneNumber?.message}
        {...register(`relatives.${index}.phoneNumber`, {
          pattern: {
            value: /^([0-9]|\s)+$/,
            message: t`Telefonnumret får bara innehålla siffror`,
          },
        })}
      />
      <InputField
        label={t`Relation`}
        showOptionalLabel={false}
        errorMessage={errors.relatives?.[index]?.relation?.message}
        {...register(`relatives.${index}.relation`, {
          required: {
            value: true,
            message: t`Ange relation`,
          },
        })}
      />
      <Checkbox
        label={{ text: t`Sammanboende` }}
        errorMessage={errors.relatives?.[index]?.isLivingTogether?.message}
        {...register(`relatives.${index}.isLivingTogether`)}
      />
      <PlainButton type="button" onClick={onRemove}>
        <Trans>Ta bort anhörig</Trans>
      </PlainButton>
      <div className={styles.separator} />
    </Form.Section>
  );
};

const RelativesInput = ({
  relativesFieldConfig,
}: {
  relativesFieldConfig: IInformationFieldConfigValue;
}) => {
  const { control } = useFormContext<RelativesInputFields>();
  const { t } = useLingui();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "relatives",
  });

  const addRelative = () => {
    append({
      id: generateRandomUUID(),
      name: "",
      phoneNumber: undefined,
      relation: "",
      isLivingTogether: false,
    });
  };

  return (
    <Fieldset
      legend={
        relativesFieldConfig === informationFieldConfigValueSchema.enum.optional
          ? t`Anhöriga (valfri)`
          : t`Anhöriga`
      }
      visualStyle="framed"
    >
      <Form.Section>
        {fields.map((field, index) => (
          <RelativeInput
            key={field.id}
            index={index}
            onRemove={() => remove(index)}
          />
        ))}
        <PlainButton type="button" onClick={addRelative}>
          <Trans>Lägg till anhörig</Trans>
        </PlainButton>
      </Form.Section>
    </Fieldset>
  );
};

export default RelativesInput;
